import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './LoginScreen.css';
import config from '../config';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export default function LoginScreen({ onLogin, onSwitchToRegister }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const verified = searchParams.get('verified');
    const errorParam = searchParams.get('error');
    if (verified === '1') {
      setSuccess('Your account has been verified. You can now sign in.');
      setSearchParams({}, { replace: true });
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const canSubmit = useMemo(() => {
    return isValidEmail(email) && password.trim().length >= 6 && !isSubmitting;
  }, [email, password, isSubmitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (password.trim().length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error ?? data?.message ?? 'Login failed. Please try again.');
      }

      // Store JWT token
      if (data.jwt) {
        localStorage.setItem('vt_dashboard_token', data.jwt);
      }

      onLogin(data);
    } catch (err) {
      setError(err?.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vt-login">
      <div className="vt-loginShell">
        <div className="vt-loginBrand" aria-label="Veritage">
          <div className="vt-loginLogo" aria-hidden="true">
            <svg
              width="32"
              height="32"
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <g transform="translate(100,100)" stroke="currentColor" strokeWidth="5" strokeLinecap="round">
                <g>
                  <circle cx="0" cy="0" r="82" fill="none" stroke="none" />
                  {[...Array(20)].map((_, i) => (
                    <line
                      key={`outer-${i}`}
                      x1="0"
                      y1="-82"
                      x2="0"
                      y2="-70"
                      transform={`rotate(${i * 18})`}
                    />
                  ))}
                </g>
                <g>
                  <circle cx="0" cy="0" r="60" fill="none" stroke="none" />
                  {[...Array(20)].map((_, i) => (
                    <line
                      key={`inner-${i}`}
                      x1="0"
                      y1="-56"
                      x2="0"
                      y2="-48"
                      transform={`rotate(${i * 18})`}
                    />
                  ))}
                </g>
              </g>
            </svg>
          </div>
          <div className="vt-loginBrandText">
            <div className="vt-loginBrandName">Veritage</div>
            <div className="vt-loginBrandTag vt-muted">Enterprise portal</div>
          </div>
        </div>

        <div className="vt-loginCard vt-surface">
          <div className="vt-loginHeader">
            <h1 className="vt-loginTitle">Sign in</h1>
            <p className="vt-loginSubtitle vt-muted">
              Use your organization credentials to access the dashboard.
            </p>
          </div>

          <form className="vt-loginForm" onSubmit={handleSubmit}>
            <label className="vt-field">
              <span className="vt-fieldLabel">Email</span>
              <input
                className="vt-input"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <label className="vt-field">
              <span className="vt-fieldLabel">Password</span>
              <input
                className="vt-input"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            {error && (
              <div className="vt-loginError" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div role="status" style={{ color: 'var(--vt-success, #0a7)', marginBottom: '1rem', fontSize: '14px' }}>
                {success}
              </div>
            )}

            <button className="vt-btn vt-btn--primary vt-loginSubmit" type="submit" disabled={!canSubmit}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="vt-loginFooter">
              <button
                type="button"
                className="vt-link"
                onClick={onSwitchToRegister}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '13px',
                  padding: 0,
                }}
              >
                Don't have an account? Sign up
              </button>
            </div>
          </form>
        </div>

        <div className="vt-loginMeta vt-muted">
          <span>© {new Date().getFullYear()} Veritage</span>
          <span className="vt-loginDot" aria-hidden="true">
            •
          </span>
          <span>Security & compliance by design</span>
        </div>
      </div>
    </div>
  );
}

